import { Modal } from "~/components/Modal";
import { Button } from "~/components/ui/button";
import { Anchor } from "~/components/Anchor";
import { Download, ArrowRight } from "lucide-react";
import { Link } from "~/components/Link";
import { Routes } from "~/routes";
import { OrganizationDownloadUsage } from "../../types";
import { PlanTypes } from "~/modules/user/store/types";
import { cn } from "~/lib/utils";
import { DataExportModalHeader } from "./DataExportModalHeader";
import { getUsageBreakdown, getViewState } from "./types";

// // // //

/**
 * DataExportModal
 *
 * Displays a modal dialog that walks the user through a data download:
 * - checking their organization's download usage
 * - confirming the download (and showing remaining quota) for metered plans
 * - blocking the download if the plan's rolling limit has been exceeded
 * - surfacing the download URL when it's ready
 */
export function DataExportModal(props: {
    show: boolean;
    usage: OrganizationDownloadUsage | null;
    downloadUrl: string | null;
    isLoadingDownloadUrl: boolean;
    planType: PlanTypes;
    onHide: () => void;
    onConfirmDownload: () => void;
}) {
    const {
        show,
        usage,
        downloadUrl,
        isLoadingDownloadUrl,
        planType,
        onHide,
        onConfirmDownload,
    } = props;

    // Define pro-plan flag
    const isProPlan = planType === PlanTypes.PREMIUM_ALL_ACCESS;

    // Get the current view state
    const viewState = getViewState({
        usage,
        isLoadingDownloadUrl,
        downloadUrl,
    });

    // Get the usage breakdown
    const usageBreakdown = usage ? getUsageBreakdown(usage) : null;

    return (
        <Modal
            show={show}
            onHide={() => {
                onHide();
            }}
        >
            <div className="w-full max-w-md rounded-2xl bg-card p-8 border">
                {/* Status Section - fixed minimum height to prevent layout shift */}
                <div className="flex min-h-[180px] flex-col items-center justify-center text-center">
                    <DataExportModalHeader
                        viewState={viewState}
                        usageBreakdown={usageBreakdown}
                    />

                    {/* Render spacer for loading/preparing states */}
                    {(viewState === "loading_usage" ||
                        viewState === "preparing_download") && (
                        <div className="mt-3 h-5" />
                    )}

                    {viewState === "limit_exceeded" && (
                        <Link
                            href={Routes.salesPage}
                            analyticsLabel="DataDownloadInfoModal - limit exceeded pricing link"
                            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-link hover:text-link-hover transition-colors"
                        >
                            Explore Enterprise Plan
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    )}

                    {viewState === "awaiting_confirmation" &&
                        usageBreakdown && (
                            <button
                                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-link hover:text-link-hover transition-colors cursor-pointer"
                                onClick={() => {
                                    onConfirmDownload();
                                }}
                            >
                                <Download className="h-4 w-4" />
                                Confirm and Download
                            </button>
                        )}

                    {viewState === "download_ready" && downloadUrl && (
                        <Anchor
                            href={downloadUrl}
                            analyticsLabel="DataDownloadInfoModal - manual download link"
                            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-link hover:text-link-hover transition-colors"
                        >
                            <Download className="h-4 w-4" />
                            Download manually
                        </Anchor>
                    )}
                </div>

                {/* Divider */}
                <div
                    className={cn("h-px bg-border", {
                        "mt-3 mb-6": isProPlan,
                        "my-6": !isProPlan,
                    })}
                />

                {/* Upsell Section */}
                {!isProPlan && (
                    <div className="rounded-xl bg-muted p-5">
                        <h3 className="text-sm font-semibold text-foreground">
                            Need data for commercial use?
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                            Get full API access to our comprehensive market
                            dataset on tokenized real-world assets. Special
                            rates for researchers and academic institutions.
                        </p>
                        <Link
                            href={Routes.salesPage}
                            analyticsLabel="DataDownloadInfoModal - contact email"
                            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-link hover:text-link-hover transition-colors"
                        >
                            Explore Enterprise Plan
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                )}

                <Button
                    size="sm"
                    variant="primaryLight"
                    className="mt-6 rounded-full w-full"
                    onClick={() => {
                        onHide();
                    }}
                >
                    Close
                </Button>
            </div>
        </Modal>
    );
}
